///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment } from "react";

import ErrorIcon from "../../icons/ErrorIcon";
import TruckloadIcon from "../../icons/TruckloadIcon";
import LessThanTruckloadIcon from "../../icons/LessThanTruckloadIcon";
import IntermodalIcon from "../../icons/IntermodalIcon";
import {
  status as modeStatus,
  mode as modeTypes,
  awardStatus as bidAwardStatus
} from "../../../helpers/types";
import messages from "../../../helpers/messages";

const Step = ({
  start,
  end,
  checkpoint = true,
  complete,
  mode,
  width = "one-quarter",
  text,
  lost,
  rejected
}) => {
  const rejetedFill = "#9A9A9A";
  const nonRejectedFill = "#343434";
  return (
    <Fragment>
      <div
        className={
          "grid__cell text-right " +
          width +
          (complete ? " complete " : " incomplete ") +
          (start ? "start " : "") +
          (end ? " end " : "") +
          (lost ? " lost " : "") +
          (rejected ? " rejected " : "")
        }
      >
        {text && (
          <p
            className="fr inline-block pr-20 m-0 pt-4"
            data-testid="award-status"
          >
            {text}
          </p>
        )}
        {(end || rejected) && complete && mode === modeTypes.tl ? (
          <TruckloadIcon
            width="29px"
            height="20px"
            viewBox="8 -5 42 34"
            secondaryFill={rejected ? rejetedFill : nonRejectedFill}
            fill={rejected ? rejetedFill : nonRejectedFill}
            className="mr-16 mt-2"
          />
        ) : (
          ((end || rejected) && complete) || "\u00A0"
        )}
        {(end || rejected) && complete && mode === modeTypes.ltl ? (
          <LessThanTruckloadIcon
            width="29px"
            height="20px"
            viewBox="8 -5 42 34"
            secondaryFill={rejected ? rejetedFill : nonRejectedFill}
            fill={rejected ? rejetedFill : nonRejectedFill}
            className="mr-16 mt-2"
          />
        ) : (
          ((end || rejected) && complete) || "\u00A0"
        )}
        {(end || rejected) && complete && mode === modeTypes.imdl ? (
          <IntermodalIcon
            width="29px"
            height="20px"
            viewBox="8 -5 42 34"
            className="mr-16 mt-2"
            fill={rejected ? rejetedFill : nonRejectedFill}
          />
        ) : (
          ((end || rejected) && complete) || "\u00A0"
        )}
      </div>
      {checkpoint && (
        <div className="grid__cell checkpoint pos-absolute">&nbsp;</div>
      )}
    </Fragment>
  );
};

export const ProgressBar = ({ loading, error, modes }) => (
  <div className="progress-bar grid one-whole pt-28">
    <div className="grid__cell one-eighth caption pl-20">
      <p className="mb-0">{messages.pending.defaultMessage}</p>
    </div>
    <div className="grid__cell one-quarter caption text-center">
      <p className={"mb-0 " + (error ? "" : "border-point")}>
        {messages.inProgress.defaultMessage}
      </p>
    </div>
    <div className="grid__cell one-quarter caption text-center">
      <p className={"mb-0 " + (error ? "" : "border-point")}>
        {messages.submitted.defaultMessage}
      </p>
    </div>
    <div className="grid__cell one-quarter caption text-center">
      <p className={"mb-0 " + (error ? "" : "border-point")}>
        {messages.feedback.defaultMessage}
      </p>
    </div>
    <div className="grid__cell one-eighth caption pr-20 text-right">
      <p className="mb-0">{messages.closed.defaultMessage}</p>
    </div>

    {(loading || error) && (
      <div className="grid one-whole bar start end mt-16 pos-relative">
        {error && (
          <Fragment>
            <div className="grid__cell one-third">
              <div className={"grid__cell two-tenths start end grey-bar"}>
                &nbsp;
              </div>
            </div>
            <div className="grid__cell one-third mt-2 text-center">
              <ErrorIcon
                className="pos-relative inline-block progress-bar-error-icon"
                height="16px"
                width="16px"
                fill="#C9CACC"
              />
              <span className="ml-8 text-light-grey font-12 ">
                {messages.pleaseRefreshTheBrowser.defaultMessage}
              </span>
            </div>
          </Fragment>
        )}
        {loading && (
          <Fragment>
            <Step
              width="one-twelfth grey-bar"
              start
              complete={true}
              checkpoint={false}
            />
            <Step width="one-sixth" complete={false} />
            <Step complete={false} />
            <Step complete={false} />
          </Fragment>
        )}
      </div>
    )}
    {error ||
      loading ||
      modes.map(({ status, mode, awardStatus }) => {
        let text = "";
        if (
          awardStatus === bidAwardStatus.won &&
          status === modeStatus.closed
        ) {
          text = messages.awarded.defaultMessage;
        } else if (awardStatus === bidAwardStatus.lost) {
          text = messages.lost.defaultMessage;
        }
        return (
          <div
            key={mode}
            className="grid one-whole bar start end mt-16 pos-relative"
          >
            {status === modeStatus.pending && (
              <Fragment>
                <Step
                  width="one-twelfth"
                  mode={mode}
                  complete={true}
                  start
                  end
                  checkpoint={false}
                ></Step>
                <Step
                  width="one-sixth"
                  mode={mode}
                  complete={status >= modeStatus.inProgress}
                  start
                  end
                ></Step>
              </Fragment>
            )}
            {status === modeStatus.rejected && (
              <Fragment>
                <Step
                  width="one-twelfth"
                  mode={mode}
                  complete={true}
                  checkpoint={false}
                  start
                  lost={false}
                  rejected={true}
                ></Step>
                <Step width="one-sixth" mode={mode} rejected={true}></Step>
              </Fragment>
            )}
            {status === modeStatus.pending ||
              status === modeStatus.rejected || (
                <Step
                  mode={mode}
                  complete={true}
                  start
                  end={status === modeStatus.inProgress}
                  lost={awardStatus === bidAwardStatus.lost}
                  rejected={status === modeStatus.rejected}
                ></Step>
              )}
            <Step
              mode={mode}
              complete={status >= modeStatus.submitted}
              end={status === modeStatus.submitted}
              lost={awardStatus === bidAwardStatus.lost}
              rejected={status === modeStatus.rejected}
            ></Step>
            <Step
              mode={mode}
              complete={status >= modeStatus.feedback}
              end={status === modeStatus.feedback}
              lost={awardStatus === bidAwardStatus.lost}
              rejected={status === modeStatus.rejected}
            ></Step>
            <Step
              mode={mode}
              complete={status >= modeStatus.closed}
              end={true}
              checkpoint={false}
              text={text}
              lost={awardStatus === bidAwardStatus.lost}
              rejected={status === modeStatus.rejected}
            ></Step>
          </div>
        );
      })}
  </div>
);

export default ProgressBar;
